import {
  ChangeEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import WaveSurfer from 'wavesurfer.js';
import {useQueryClient} from '@tanstack/react-query';
import Regions, {Region} from 'wavesurfer.js/dist/plugins/regions';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import * as yup from 'yup';
import {
  Box,
  IconButton,
  Button,
  Typography,
  Stack,
  Skeleton,
  TextField,
  Tooltip,
} from '@mui/material';
import {DataGridPro} from '@mui/x-data-grid-pro';
import PauseIcon from '@mui/icons-material/Pause';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {GridColDef, GridRowId, GridRowSelectionModel} from '@mui/x-data-grid';
import IconPlay from '../../asset/icons/icon_play.svg';
import StopIcon from '@mui/icons-material/Stop';
import * as lamejs from 'lamejs';
import {apiGet, apiPost} from '../../axiosConfig';
import {useStoreSelector} from '../../store/configstore';
import {useStationContext} from '../../components/models/stations/StationContext';
import {companyIdSelector} from '../../store/Slices/companySlice';
import DialogApiRequestStatus from '../../components/dialogs/DialogApiRequestStatus';
import {getRecordingVerificationTaskMessagesUrl} from '../../react_query/messages/task/ListRecordingVerificationMessages';
import {useNavigate} from 'react-router-dom';
import {useRqCheckClipNameUnique} from '../../react_query/music_genres/ListGenresGrouped';
import {UpdateRegions} from './WaveformFunctions';
import {UserCanAny} from '../../components/UserCan';
import IconDelete from '../../asset/icons/icon_delete.svg';
import {getMediaTracksProPaginateApiPath} from '../../react_query/media_tracks/ListMediaTrackPaginatPro';

interface WaveSurferOptions {
  container: HTMLElement | string;
  barWidth: number;
  barRadius: number;
  cursorWidth: number;
  waveColor: string;
  progressColor: string;
  backend: 'WebAudio' | 'MediaElement';
  mediaType: 'audio' | 'video';
  height: number;
  cursorColor: string;
  responsive: boolean;
  hideScrollbar: boolean;
  partialRender: boolean;
  normalize: boolean;
}
export interface IWaveFormRegions {
  id: string;
  startTime: string;
  endTime: string;
  total_duration: string;
  clip_name?: string;
  isClipNameDuplicate?: boolean;
}

export const formWaveSurferOptions = (
  ref: HTMLElement | string,
): WaveSurferOptions => ({
  container: ref,
  barWidth: 3,
  barRadius: 3,
  cursorWidth: 3,
  waveColor: '#6666ff',
  progressColor: '#6666ff',
  backend: 'MediaElement',
  mediaType: 'audio',
  height: 120,
  cursorColor: 'black',
  responsive: true,
  hideScrollbar: true,
  partialRender: false,
  normalize: true,
});

// Flag to hide the preview button, if needed we can reuse it in the future
const hidePreview = false;

const secondsToTime = (secs: number) => {
  const secondsGiven = Math.round(secs);
  let minutes = Math.floor(secondsGiven / 60) || 0;
  const seconds = secondsGiven - minutes * 60 || 0;
  let timeString = '';
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    timeString += `${hours < 10 ? '0' : ''}${hours}:`;
    minutes %= 60;
  }
  timeString += `${minutes < 10 ? '0' : ''}${minutes}:`;
  timeString += `${seconds < 10 ? '0' : ''}${seconds}`;
  return timeString;
};

function floatTo16BitPCMS(input: Float32Array, output: Int16Array) {
  for (let i = 0; i < input.length; i++) {
    let s = Math.max(-1, Math.min(1, input[i] as number));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
}
const fileNameValidationSchema = yup.object().shape({
  clip_name: yup
    .string()
    .required('Title is required')
    .min(3, 'Title is too short')
    .max(50, 'Title is too long'),
});
interface ITrackProps {
  id: number;
  task_id: number;
  title: string;
  artist: string;
  url: string;
}
type Props = {
  data: ITrackProps;
  type: string; // pss, clip
  model?: string; // message, song-request
  walkupType?: string;
  clipType?: string;
  clipResetTab?: () => void;
  handleCloseViewModal?: () => void;
};
export default function Waveform({
  data,
  type,
  model = 'message',
  walkupType,
  clipType,
  clipResetTab,
  handleCloseViewModal,
}: Props) {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>();
  const cursorPositionRef = useRef<number | null>(null);

  const wavesurfer = useRef<WaveSurfer | null>(null);
  const [regions, setRegions] = useState<IWaveFormRegions[]>([]);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const wsRegions = useRef<Regions | null>(null);
  const regiontoPlay: Region[] = [];
  const regionsRef = useRef(regions);
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null);
  const [filePreviewBtn, setFilePreviewBtn] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [validation, setValidation] = useState(false);
  const companyId = useStoreSelector(companyIdSelector);
  const [apiState, setApiState] = useState({status: '', error: null});
  const [blob, setBlob] = useState<BlobPart>();
  const {stations, setStations} = useStationContext();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [liveId, setLiveId] = useState('');
  const [isRegionPlaying, setIsRegionPlaying] = useState<string | null>(null);
  const navigate = useNavigate();

  const {
    data: uniqueCheck,
    isError,
    isFetching,
  } = useRqCheckClipNameUnique(companyId, name, Boolean(name));
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);
  const handlePlayPauseClick = () => {
    wavesurfer.current?.playPause();
    setIsPlaying(prevIsPlaying => !prevIsPlaying);
  };

  useEffect(() => {
    regionsRef.current = regions;
  }, [regions]);

  const regionValidation = useCallback(updatedRegions => {
    yup
      .array()
      .of(fileNameValidationSchema)
      .validate(updatedRegions)
      .then(() => {
        setValidation(true);
      })
      .catch(() => {
        setValidation(false);
      });
  }, []);

  const handleRegionCreated = useCallback(
    (region: Region) => {
      const regionObj = {
        id: region.id,
        total_duration: secondsToTime(region.end - region.start),
        startTime: secondsToTime(region.start),
        endTime: secondsToTime(region.end),
        clip_name: '',
      };
      setRegions([...regionsRef.current, {...regionObj}]);
      setFilePreviewBtn(true);
      regionValidation([...regionsRef.current, {...regionObj}]);
    },
    [regionValidation],
  );

  const handleRegionUpdated = (region: Region) => {
    const regionId = region.id;
    const updatedRegion = [...regionsRef.current];
    const updatedRegions = updatedRegion.map(regions => {
      const regionObj = {
        id: region.id,
        total_duration: secondsToTime(region.end - region.start),
        startTime: secondsToTime(region.start),
        endTime: secondsToTime(region.end),
      };
      if (regions.id === regionId) {
        return regionObj;
      } else {
        return regions;
      }
    });
    setRegions(updatedRegions);
    setIsRegionPlaying(null);
  };
  const handleRegionOut = () => {
    wavesurfer.current?.stop();
    setIsRegionPlaying(null);
  };
  const handleRegionClicked = (region: Region) => {
    region.play();
    setIsRegionPlaying(null);
  };

  useEffect(() => {
    if (data.url && waveformRef.current) {
      const options = formWaveSurferOptions(waveformRef.current);
      wavesurfer.current = WaveSurfer.create(options);
      apiGet('/v1/proxy-song', {
        params: {
          url: data.url,
        },
        responseType: 'blob',
      }).then(response => {
        const audioBlob = response.data;

        wavesurfer.current?.loadBlob(audioBlob);
      });
      wavesurfer.current.on('decode', () => {
        const decodedData = wavesurfer.current?.getDecodedData();
        if (decodedData) {
          setAudioBuffer(decodedData);
        }
      });
      wsRegions.current = wavesurfer.current?.registerPlugin(Regions.create());
      wavesurfer.current?.registerPlugin(TimelinePlugin.create());
      wsRegions.current?.enableDragSelection({
        color: 'rgba(89, 19, 19, 0.37)',
      });
      wsRegions.current?.on('region-created', handleRegionCreated);
      wsRegions.current?.on('region-updated', handleRegionUpdated);
      wsRegions.current?.on('region-out', handleRegionOut);
      wsRegions.current?.on('region-clicked', handleRegionClicked);
    } else {
      setRegions([]);
      setStations([]);
      wavesurfer.current = null;
    }
    return () => {
      // Cleanup event listeners when the component unmounts or dependencies change.
      wsRegions.current?.un('region-created', handleRegionCreated);
      wsRegions.current?.un('region-updated', handleRegionUpdated);
      wsRegions.current?.un('region-out', handleRegionOut);
      wsRegions.current?.un('region-clicked', handleRegionClicked);
      wavesurfer.current?.destroy();
      setRegions([]);
      setValidation(false);
    };
  }, [data.url, handleRegionCreated, setStations]);

  const handleStop = useCallback(() => {
    wavesurfer.current?.stop();
    setIsPlaying(false);
  }, []);

  const handlePlayPause = useCallback(
    (id: string, type = 'play') => {
      if (wavesurfer.current) {
        if (isRegionPlaying) {
          handleStop();
          setTimeout(() => {
            playRegion(id, type);
          }, 50);
          return;
        }
        playRegion(id, type);
      }
    },
    [isRegionPlaying, handleStop],
  );

  const playRegion = (id: string, type: string) => {
    const regiontoPlay = wsRegions.current
      ?.getRegions()
      .find((obj: Region) => obj.id === id);

    if (regiontoPlay) {
      if (type === 'play') {
        regiontoPlay.play();
        setIsRegionPlaying(id);
      } else {
        wavesurfer.current?.stop();
        setIsRegionPlaying(null);
      }
    }
  };

  const handleDelete = useCallback(
    (id: string) => {
      setFilePreviewBtn(true);
      setSelectedRows([]);
      const regionslist = [...regionsRef.current];
      const updatedRegions: IWaveFormRegions[] = [];
      regionslist.forEach((regions: IWaveFormRegions) => {
        if (regions.id !== id) {
          updatedRegions.push(regions);
        }
      });
      const regiontoPlay = wsRegions.current
        ?.getRegions()
        .find((obj: Region) => obj.id === id);
      regiontoPlay?.remove();
      setRegions(updatedRegions);
      regionValidation(updatedRegions);
      setIsRegionPlaying(null);
    },
    [regionValidation],
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedRegions = UpdateRegions({
      regions: regionsRef.current,
      id: event.target.id,
      key: 'clip_name',
      value: event.target.value.trim().length !== 0 ? event.target.value : '',
    });
    setRegions(updatedRegions);
    cursorPositionRef.current = event.target.selectionStart;

    yup
      .array()
      .of(fileNameValidationSchema)
      .validate(updatedRegions)
      .then(() => {
        setLiveId(event.target.id);
        if (event.target.value.trim().length !== 0) {
          setName(event.target.value);
        }
        setValidation(true);
      })
      .catch(() => {
        setValidation(false);
      });
  };

  // Use layout effect to synchronously restore cursor position
  useLayoutEffect(() => {
    if (inputRef.current && cursorPositionRef.current !== null) {
      inputRef.current.setSelectionRange(
        cursorPositionRef.current,
        cursorPositionRef.current,
      );
    }
  });
  useEffect(() => {
    if (!isError && !isFetching && uniqueCheck) {
      const updatedRegions = UpdateRegions({
        regions: regionsRef.current,
        id: liveId,
        key: 'isClipNameDuplicate',
        value: uniqueCheck.name_exist,
      });
      setRegions(updatedRegions);
    }
  }, [isError, isFetching, liveId, uniqueCheck]);
  const isWalkupOrCustom =
    walkupType === 'walkupclip' || clipType === 'customClip';
  const mlClass = isWalkupOrCustom ? 'ml-12' : 'ml-15';
  const ml = isWalkupOrCustom ? '' : 'ml-15';
  let columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'startTime',
        headerName: 'Start Time',
        width: 100,
        headerClassName: 'text-15 mt-8',
        cellClassName: 'border-bottom-none',
      },
      {
        field: 'endTime',
        headerName: 'End Time',
        width: 100,
        headerClassName: 'text-15 mt-8 ml-15',
        cellClassName: 'ml-15 border-bottom-none',
      },
      {
        field: 'total_duration',
        headerName: 'Total Duration',
        width: 150,
        headerClassName: 'text-15 mt-8 ml-15',
        cellClassName: 'ml-15 border-bottom-none',
      },
      {
        field: 'clip_name',
        headerName: 'Clip Name',
        headerClassName: `text-15 mt-8 ${mlClass}`,
        cellClassName: `${mlClass} border-bottom-none`,
        width: 300,
        renderCell: params => (
          <TextField
            error={
              params.row.isClipNameDuplicate
                ? params.row.isClipNameDuplicate
                : false
            }
            value={params.row.clip_name}
            variant='outlined'
            id={String(params.id)}
            placeholder='3-50 characters Allowed'
            onChange={handleChange}
            className='text-wrap-helper'
            onSelect={e => {
              cursorPositionRef.current = (
                e.target as HTMLInputElement
              ).selectionStart;
            }}
            inputRef={inputRef}
            onKeyDown={e => {
              if (e.key === ' ') {
                e.stopPropagation();
              }
            }}
            helperText={
              params.row.isClipNameDuplicate &&
              params.row.isClipNameDuplicate === true
                ? 'This clip name is already taken. Please provide a unique clip name to proceed.'
                : null
            }
          />
        ),
      },
      {
        field: 'actions',
        sortable: false,
        headerName: 'Actions',
        width: 150,
        headerClassName: `${ml} text-15 mt-8`,
        cellClassName: `${ml} border-bottom-none`,
        renderCell: params => (
          <Box className='flex items-center'>
            {isRegionPlaying === params.id ? (
              <StopIcon
                onClick={() => handlePlayPause(String(params.id), 'pause')}
              />
            ) : (
              <img
                src={IconPlay}
                alt='Play'
                className='cursor-pointer '
                onClick={() => handlePlayPause(String(params.id), 'play')}
              />
            )}
            <IconButton color='error' className='ml-3'>
              <img
                src={IconDelete}
                alt='Delete'
                className='cursor-pointer'
                onClick={() => handleDelete(String(params.id))}
              />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handleDelete, handlePlayPause, isRegionPlaying, mlClass, ml],
  );
  if (type === 'pss') {
    const updatedColumns = columns.filter(
      column => column.field !== 'clip_name',
    );
    columns = updatedColumns;
  }
  const mergeAudio = useCallback(
    async function (audioList: Region[]) {
      const trackDetails = [];
      let channelLength = 0;
      if (audioBuffer && typeof audioList !== 'undefined') {
        for (const i in audioList) {
          const regionDuration =
            (audioList[i]?.end as number) - (audioList[i]?.start as number);
          const startPoint = Math.floor(
            ((audioList[i]?.start as number) * audioBuffer.length) /
              (wavesurfer.current?.getDuration() as number),
          );
          const endPoint = Math.ceil(
            ((audioList[i]?.end as number) * audioBuffer.length) /
              (wavesurfer.current?.getDuration() as number),
          );
          const audioLength = endPoint - startPoint;
          channelLength = channelLength + audioLength;
          const trackDetail = {
            regionDuration: regionDuration,
            startPoint: startPoint,
            endPoint: endPoint,
            audioLength: audioLength,
          };
          trackDetails.push(trackDetail);
        }
        const audioContext = new window.AudioContext();
        const mergedBuffer = audioContext.createBuffer(
          audioBuffer.numberOfChannels,
          channelLength,
          audioContext.sampleRate,
        );
        const channelData =
          audioBuffer.numberOfChannels === 1
            ? new Array(new Float32Array(channelLength))
            : [
                new Float32Array(channelLength),
                new Float32Array(channelLength),
              ];
        for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
          let startLength = 0;
          for (const j in trackDetails) {
            if (trackDetails[j]) {
              channelData[i]?.set(
                audioBuffer
                  .getChannelData(i)
                  .slice(
                    trackDetails[j]!.startPoint,
                    trackDetails[j]!.endPoint,
                  ),
                startLength,
              );
              startLength = trackDetails[j]!.audioLength;
            }
          }
        }
        for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
          if (channelData[i] !== undefined) {
            mergedBuffer.copyToChannel(channelData[i]!, i);
          }
        }
        return channelData;
      }
      return true;
    },
    [audioBuffer],
  );

  const getMP3Blob = async (channelData: Float32Array[]) => {
    // Create an audio context
    const audioContext = new AudioContext();
    // Interleave the channel data
    const mp3Blob = await encodeMP3(channelData, audioContext.sampleRate);
    return mp3Blob;
  };

  async function encodeMP3(channelData: Float32Array[], sampleRate: number) {
    return new Promise<Blob>(resolve => {
      const buffer = [];
      const mp3Encoder = new lamejs.Mp3Encoder(
        channelData.length,
        sampleRate,
        128,
      );
      if (channelData && channelData.length === 1) {
        for (const data of channelData) {
          if (data && data.length > 0) {
            const samples = new Int16Array(data.length);
            floatTo16BitPCMS(data, samples);
            for (let j = 0; j < samples.length; j += 1152) {
              const chunk = samples.subarray(
                j,
                Math.min(j + 1152, samples.length),
              );
              const mp3buf = mp3Encoder.encodeBuffer(chunk);
              buffer.push(new Int8Array(mp3buf));
            }
          }
        }
      }
      if (channelData && channelData.length === 2) {
        if (channelData[0] && channelData[1]) {
          let right = new Int16Array(channelData[0].length);
          floatTo16BitPCMS(channelData[0], right);
          let left = new Int16Array(channelData[1].length);
          floatTo16BitPCMS(channelData[1], left);
          for (let i = 0; i < channelData[0].length; i += 1152) {
            let leftChunk = left.subarray(i, i + 1152);
            let rightChunk = right.subarray(i, i + 1152);
            const mp3buf = mp3Encoder.encodeBuffer(leftChunk, rightChunk);
            buffer.push(new Int8Array(mp3buf));
          }
        }
      }
      const blob = new Blob(buffer, {type: 'audio/mp3'});
      resolve(blob);
    }).catch(() => {
      setFilePreviewBtn(false);
    });
  }

  async function handleFile() {
    selectedRows.forEach((regionId: GridRowId) => {
      const regions = wsRegions.current
        ?.getRegions()
        .find((obj: Region) => obj.id === regionId);
      if (regions) {
        regiontoPlay.push(regions);
      }
    });
    const channelData = await mergeAudio(regiontoPlay);
    const res = await getMP3Blob(channelData as Float32Array[]);
    if (res instanceof Blob) {
      setBlob(res);
      setFilePreviewBtn(false);
      const downloadLink = URL.createObjectURL(res);
      const audioElement = document.getElementById('previewaudio');
      if (audioElement instanceof HTMLAudioElement) {
        audioElement.src = downloadLink;
        audioElement.style.display = 'block';
      }
    }
  }
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    const selectedRowIds = Array.from(newSelection);
    setSelectedRows(selectedRowIds);
  };

  const enablePendingDialogClip = () => {
    setApiState({status: 'pending', error: null});
    setTimeout(handleClip, 5, true);
  };

  const handleClip = async () => {
    setApiState({status: 'pending', error: null});
    const formData = new FormData();
    const availRegions = wsRegions.current?.getRegions();
    if (!availRegions) return;
    formData.append('media_id', String(data.id));
    const promises = availRegions.map(async (region, index) => {
      const foundRegion = regionsRef.current.find(
        (obj: IWaveFormRegions) => obj.id === region.id,
      );
      const duration = Math.round(region.end - region.start);
      const channelData = await mergeAudio([region]);
      const res = await getMP3Blob(channelData as Float32Array[]);
      if (res instanceof Blob) {
        const file = new File([res], String(foundRegion?.clip_name));
        formData.append(`files[${index}]`, file);
        formData.append(
          `names[${index}]`,
          String(foundRegion?.clip_name?.trim()),
        );
        formData.append(`duration[${index}]`, `${duration}`);
      }
    });
    await Promise.all(promises);
    apiPost(`/v1/company/${companyId}/on-demand-playlist/create-clip`, formData)
      .then(() => {
        setApiState({status: 'success', error: null});
        if (type === 'clip' && walkupType !== 'walkupClip') {
          navigate('/music/on-demand', {
            state: {
              setCustomClip: true,
              stationId: stations && stations[0] ? stations[0].id : 0,
            },
          });
          clipResetTab?.();
        } else if (type === 'clip' && walkupType === 'walkupClip') {
          queryClient.invalidateQueries({
            queryKey: [
              getMediaTracksProPaginateApiPath(companyId),
              {track_category: 4},
            ],
          });
        }
        handleCloseViewModal?.();
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  };

  const handleFinalVersion = () => {
    if (blob) {
      const formData = new FormData();
      const file = new File([blob], data.title);
      formData.append(`file`, file);
      setApiState({status: 'pending', error: null});
      apiPost(`/v1/task/recording/${model}/${data.id}`, formData)
        .then(() => {
          setSelectedRows([]);
          setRegions([]);
          wsRegions.current?.getRegions().map((obj: Region) => obj.remove());
          queryClient.invalidateQueries({
            queryKey: [getRecordingVerificationTaskMessagesUrl(data.task_id)],
            refetchType: 'active',
          });

          setApiState({status: 'success', error: null});
        })
        .catch(error => {
          setApiState({status: 'error', error});
        });
    }
  };
  const handleClose = () => {
    setApiState({status: '', error: null});
  };
  return (
    <Box style={{height: '100%', position: 'relative'}}>
      <div
        id='waveform'
        className='mt-5 black-variant1'
        ref={waveformRef}></div>
      {waveformRef.current &&
      wavesurfer.current &&
      wavesurfer.current?.getDuration() > 0 ? (
        <Stack direction='row' sx={{justifyContent: 'space-between'}}>
          <Tooltip title={data.title}>
            <Typography className='text-center p-2 mt-10'>
              Title:{' '}
              {data.title.length > 25
                ? `${data.title.slice(0, 20)}...`
                : data.title}
            </Typography>
          </Tooltip>

          <Tooltip title={data.artist}>
            <Typography className='text-center p-2 mt-10'>
              Artist:{' '}
              {data.artist.length > 25
                ? `${data.artist.slice(0, 20)}...`
                : data.artist}
            </Typography>
          </Tooltip>
          <Typography className='text-center p-2 mt-10 ml-25'>
            Duration :{secondsToTime(wavesurfer.current?.getDuration() ?? 0)}
          </Typography>
          <Button
            onClick={handlePlayPauseClick}
            variant='contained'
            size='small'
            color='primary'
            className='mt-10 p-5'>
            {isPlaying ? (
              <PauseIcon />
            ) : (
              <img src={IconPlay} className='mr-3 w-auto h-auto' />
            )}
            {isPlaying ? 'Pause' : 'Play'}
          </Button>
          <Button
            onClick={handleStop}
            size='small'
            variant='contained'
            color='primary'
            className='mt-10 waveform-stop-button p-5 mr-5'>
            <StopCircleIcon className='mr-3 ' /> Stop
          </Button>
        </Stack>
      ) : (
        <>
          {data.url && (
            <Skeleton
              variant='rectangular'
              sx={{height: '30%', position: 'relative', bottom: '200px'}}
            />
          )}
        </>
      )}
      {regions.length > 0 && (
        <Box sx={{height: '40vh', width: '100%'}}>
          <DataGridPro
            rows={regions}
            rowHeight={140}
            columns={columns}
            disableColumnMenu
            checkboxSelection={type !== 'clip'}
            disableRowSelectionOnClick
            onRowSelectionModelChange={handleSelectionChange}
          />
          {validation && stations && stations.length > 0 && (
            <Button
              sx={{
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: 8,
                position: 'absolute',
              }}
              variant='outlined'
              onClick={enablePendingDialogClip}
              disabled={regions.some(val => val.isClipNameDuplicate === true)}>
              Save
            </Button>
          )}
        </Box>
      )}
      {selectedRows.length > 0 && hidePreview && (
        <Stack direction='row' spacing={2} sx={{mt: 2}}>
          {filePreviewBtn && (
            <Button
              variant='outlined'
              onClick={() => {
                handleFile();
              }}>
              File Preview
            </Button>
          )}
          {!filePreviewBtn && (
            <audio controls id='previewaudio' style={{display: 'none'}}>
              <source src='' type='audio/mp3' />
            </audio>
          )}
          {!filePreviewBtn && (
            <UserCanAny scope='is-admin,partner,create-clip:create'>
              <Button variant='outlined' onClick={handleFinalVersion}>
                Save & Upload File
              </Button>
            </UserCanAny>
          )}
        </Stack>
      )}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleClip}
          onEdit={handleEdit}
          onClose={handleClose}
        />
      )}
    </Box>
  );
}
